import { useCallback, useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { bulkChangeStreamFormat } from '../../api/channelBulkAdvanced';
import type { StreamFormatOption } from '../../lib/streamFormatTransform';
import { fetchChannelsIndex, type ChannelRow } from '../../api/channels';
import { getApiErrorMessage } from '../../api/http';
import { transformUrlPreview } from '../../lib/streamFormatTransform';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { useBasicEditorToolContext } from '../../context/EditorSessionContext';
import { usePlaylist } from '../../context/PlaylistContext';

function normCat(c: string | null | undefined) {
  if (c == null || c === '') return 'UNCATEGORIZED';
  const v = String(c).trim().toUpperCase();
  if (v === 'UNCATEGORIZED') return 'UNCATEGORIZED';
  return v;
}

export default function StreamFormatModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { record, refreshEditor } = useUndoRedo();
  const { selectedIds, gridActive } = useBasicEditorToolContext();
  const { playlistId } = usePlaylist();

  const [step, setStep] = useState<1 | 2>(1);
  const [option, setOption] = useState<StreamFormatOption>('ts_no_live');
  const [scope, setScope] = useState<'all' | 'selected' | 'categories'>('all');
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [catPick, setCatPick] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const c of channels) s.add(normCat(c.category));
    return [...s].sort((a, b) => a.localeCompare(b, 'en'));
  }, [channels]);

  const load = useCallback(async () => {
    if (!playlistId) return;
    try {
      const rows = await fetchChannelsIndex(playlistId);
      setChannels(rows);
      const init: Record<string, boolean> = {};
      for (const c of rows) init[normCat(c.category)] = true;
      setCatPick(init);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to fetch channels.'), 'error');
    }
  }, [playlistId, showToast]);

  useEffect(() => {
    if (!show) return;
    setStep(1);
    setOption('ts_no_live');
    if (selectedIds.length > 0 && gridActive) setScope('selected');
    else setScope('all');
    void load();
  }, [show, load, selectedIds.length, gridActive]);

  const scopeChannels = useCallback((): ChannelRow[] => {
    if (scope === 'selected') {
      const set = new Set(selectedIds.map(Number));
      return channels.filter((ch) => set.has(Number(ch.id)));
    }
    if (scope === 'categories') {
      const cats = Object.entries(catPick)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const cset = new Set(cats);
      return channels.filter((ch) => cset.has(normCat(ch.category)));
    }
    return channels;
  }, [channels, scope, selectedIds, catPick]);

  const preview = useMemo(() => {
    const sc = step === 1 ? channels : scopeChannels();
    let total = 0;
    const samples: { oldUrl: string; newUrl: string }[] = [];
    for (const ch of sc) {
      const oldUrl = String(ch.url ?? '');
      const newUrl = transformUrlPreview(oldUrl, option);
      if (newUrl !== oldUrl) {
        total++;
        if (samples.length < 6) samples.push({ oldUrl, newUrl });
      }
    }
    return { total, samples, empty: sc.length === 0 };
  }, [channels, option, step, scopeChannels]);
  const demoFromUrl = 'http://demo.com/live/AAA/BBB/1.m3u8';
  const demoToUrl = useMemo(() => transformUrlPreview(demoFromUrl, option), [option]);

  async function apply() {
    if (!playlistId) {
      showToast('Select a playlist.', 'warning');
      return;
    }
    const pid = parseInt(playlistId, 10);

    let channel_ids: number[] = [];
    let categoriesScope: string[] = [];
    if (scope === 'selected') {
      channel_ids = selectedIds.map(Number);
      if (channel_ids.length === 0) {
        showToast('No channels selected in the Basic editor.', 'warning');
        return;
      }
    } else if (scope === 'categories') {
      categoriesScope = Object.entries(catPick)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (categoriesScope.length === 0) {
        showToast('Select at least one category.', 'warning');
        return;
      }
    }

    const sc = scopeChannels();
    const undoValuesById: Record<string, string> = {};
    const redoValuesById: Record<string, string> = {};
    for (const ch of sc) {
      const id = Number(ch.id);
      if (!Number.isFinite(id)) continue;
      const oldUrl = String(ch.url ?? '');
      const newUrl = transformUrlPreview(oldUrl, option);
      if (newUrl === oldUrl) continue;
      undoValuesById[id] = oldUrl;
      redoValuesById[id] = newUrl;
    }

    if (Object.keys(redoValuesById).length === 0) {
      showToast('No changes to apply.', 'warning');
      return;
    }

    setBusy(true);
    try {
      const res = await bulkChangeStreamFormat({
        playlist_id: pid,
        option,
        scope,
        channel_ids,
        categories: categoriesScope,
      });
      if (!res.success) {
        showToast(res.message || 'Operation failed.', 'error');
        return;
      }
      showToast(res.message || 'Updated.', 'success');
      record({
        label: 'Stream format editor',
        undo: { type: 'setField', playlist_id: pid, field: 'url', valuesById: undoValuesById },
        redo: { type: 'setField', playlist_id: pid, field: 'url', valuesById: redoValuesById },
      });
      onClose();
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server connection error.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="Change stream format"
      footer={
        step === 2 ? (
          <div className="d-flex justify-content-between w-100">
            <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
              Previous
            </button>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn btn-success" disabled={busy} onClick={() => void apply()}>
                {busy ? <span className="spinner-border spinner-border-sm" /> : 'Apply'}
              </button>
            </div>
          </div>
        ) : (
          <div className="d-flex justify-content-end gap-2 w-100">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(2)}>
              Next
            </button>
          </div>
        )
      }
    >
      <div className="progress mb-3" style={{ height: 6 }}>
        <div className="progress-bar bg-primary" style={{ width: step === 1 ? '50%' : '100%' }} />
      </div>

      {step === 1 ? (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 1</p>
          <label className="form-label">Change channels streaming format</label>
          <div className="small text-muted mb-3">
            {demoFromUrl} -&gt; {demoToUrl}
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="sf1"
              checked={option === 'ts_no_live'}
              onChange={() => setOption('ts_no_live')}
            />
            <label className="form-check-label" htmlFor="sf1">
              <b>MPEG-TS: </b>without /live/
            </label>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="radio" id="sf2" checked={option === 'ts'} onChange={() => setOption('ts')} />
            <label className="form-check-label" htmlFor="sf2">
              <b>MPEG-TS: </b>with /live/
            </label>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="radio" id="sf3" checked={option === 'm3u8'} onChange={() => setOption('m3u8')} />
            <label className="form-check-label" htmlFor="sf3">
              <b>HLS: </b>m3u8 with /live/
            </label>
          </div>

          <div className="mt-3 small text-muted">
            {preview.empty ? (
              'No channels in this scope.'
            ) : (
              <>Preview: {preview.total} url(s) will be updated.</>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 2</p>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="sfSel"
              checked={scope === 'selected'}
              disabled={!gridActive || selectedIds.length === 0}
              onChange={() => setScope('selected')}
            />
            <label className="form-check-label" htmlFor="sfSel">
              {selectedIds.length} selected stream{selectedIds.length === 1 ? '' : 's'}
            </label>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="radio" id="sfCat" checked={scope === 'categories'} onChange={() => setScope('categories')} />
            <label className="form-check-label" htmlFor="sfCat">
              Streams from categories
            </label>
          </div>
          <div className="form-check mt-2 mb-2">
            <input className="form-check-input" type="radio" id="sfAll" checked={scope === 'all'} onChange={() => setScope('all')} />
            <label className="form-check-label" htmlFor="sfAll">
              All streams
            </label>
          </div>

          {scope === 'categories' ? (
            <div className="border rounded p-2 bg-light bg-opacity-50" style={{ maxHeight: 180, overflowY: 'auto' }}>
              <div className="small text-muted mb-2">Select categories:</div>
              {categories.map((c) => {
                const display = c.toUpperCase();
                return (
                  <div className="form-check mb-1" key={c}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`sf-${c}`}
                      checked={!!catPick[c]}
                      onChange={(e) => setCatPick((p) => ({ ...p, [c]: e.target.checked }))}
                    />
                    <label className="form-check-label small" htmlFor={`sf-${c}`}>
                      {display}
                    </label>
                  </div>
                );
              })}
            </div>
          ) : null}
        </>
      )}
    </SimpleModal>
  );
}
